import logger from '../utils/logger-rum';
// Import `Socket as SocketClass` to avoid class naming collision for `Socket`.
import { io, Socket as SocketClass } from 'socket.io-client';
import { difference } from 'underscore';
import { log } from '../utils/logger';
import { MediaItem } from '../stores/reducers/media';
import store from '../stores/main';
import { userDisconnected } from '../stores/actions/session';
import { EntityType } from '../types/enums';
import { Entity } from '../types/interfaces';
import { OnsiteLocationSession } from '../stores/reducers/video';
import { getEmbedSafeHeaders } from './auth';

const SERVER_NAME: string = process.env.REACT_APP_SOCKET_HOST as string;

const isDebug = window.location.search.includes('debug');
const errorTag = 'SocketError';
const socketLog = (tag: string, ...args: any[]) => {
	if (
		isDebug
		|| tag === errorTag
	) {
		console.log(`%c${tag}\n`, 'color: orange; font-weight: bold;', ...args);

		const payload = { ...args };
		if (tag === errorTag) {
			logger.error('Socket error', { tag, payload });
		} else {
			logger.log('Socket log', { tag, payload });
		}
	}
};
let disableReconnect = false;

export interface Message {
	message: any;
	type: string;
	data?: any;
}

export interface ConnectedUser {
	first_name: string;
	last_name: string;
	email: string;
	title: string;
	avatar: string;
	gr_user: number;
	// Added for migration strategy media+notes, but everything should be using gr_user moving forward
	brandlive_profile?: number;
	webrtcID?: string | number;
	screenShareWebrtcId?: string | number;
	socketId?: string;
	isProducer: boolean;
	videoMuted?: boolean;
	audioMuted?: boolean;
	location?: string;
	session: string;
	poster?: string | null;
	in_room?: boolean;
	restrictedUserState?: number;
	watcher_only?: boolean;
	selectedShowbuilderShot?: number | null;
	last_modified?: Date;
	feedWebRTCIds?: string[];
	companionMode?: boolean;
	onsiteLocationSession?: OnsiteLocationSession | null;
	shotModalId?: number | null;
	reconnectFrom?: string; // the socket id of the stale connection
	timingLeader?: boolean;
}

export interface ChatMess {
	gr_user: number;
	message: string;
	attachment?: string;
}

const entitiesToListenTo = [
	EntityType.currentShotId,
	EntityType.allShotOverlays,
	EntityType.animatedOverlayIds,
	EntityType.commsToggle,
	EntityType.scheduledStream,
	EntityType.roomState,
	EntityType.shotStartTimestamp,
	EntityType.generatedContent,
	EntityType.overlay,
	EntityType.media,
	EntityType.broadcast,
	EntityType.destinations,
	EntityType.opentokSession,
	EntityType.srtStream,
	EntityType.profileMask,
	EntityType.activeMasks,
	EntityType.onsiteLocation,
	EntityType.recentSlideDeck,
	EntityType.contentChips,
];

const deletedEntitiesToListenTo = [
	EntityType.srtStream,
	EntityType.onsiteLocation,
	EntityType.connectedUsers,
];

const entitiesIgnoredByEnabler = [
	EntityType.currentShotId,
	EntityType.allShotOverlays,
	EntityType.animatedOverlayIds,
	EntityType.roomState,
	EntityType.shotStartTimestamp,
];

interface SocketOpts {
	transports: string[],
	auth: {
		idToken: string,
		isEmbed?: boolean
	},
	query: {
		agent: string
	}
}

export default class Socket {
	static MESSAGE_TYPES = {
		CHAT_MESSAGES: 'chat_messages',
		LIKE: 'like',
		UNLIKE: 'unlike',
		DELETE_MESSAGE: 'deletemsg',
		UNDELETE_MESSAGE: 'undeletemsg',
		PRODUCT: 'product',
		APPROVE_MESSAGE: 'approve_message',
		UNAPPROVE_MESSAGE: 'unapprove_message',
		POLL_RESULTS: 'poll_results',
		FULL_MESSAGE: 'full_message',
	};

	channel: string = '';

	// it didn't like pulling the type from io, will figure that out later
	connection: SocketClass;

	listeners: Map<string, Function> = new Map();

	waitingMessages: Message[] = [];

	ready: boolean = false;

	wasHidden: boolean = false;

	rooms = new Set<string>();

	user: ConnectedUser | null = null;

	reconnected: boolean = false;

	endpoint: string = SERVER_NAME;

	onConnected: Function | undefined;

	onReconnected: Function | undefined;

	connectionTimestamp: number = Date.now();

	shouldDelete: boolean = false;

	resetConnectedUsers: boolean = false;

	options: SocketOpts

	debugListener: (event: any) => any;

	isEnablerView: boolean = false;

	constructor(channel: string, endpoint?: string, onConnected?: Function) {
		if (endpoint) {
			this.endpoint = endpoint;
		}

		const headers = getEmbedSafeHeaders();
		this.options = {
			transports: ['websocket'],
			auth: {
				idToken: headers['id-token'],
				isEmbed: headers['X-IS-EMBED'] ? true : false,
			},
			query: {
				agent: 'greenroom-web'
			}
		}

		this.isEnablerView = window.location.search.includes('enabler-shot');
		this.channel = channel;
		this.connection = io(this.endpoint, this.options).connect();
		this.setupListeners();
		this.onConnected = onConnected;
		this.debugListener = this.debugHotkey.bind(this);
		this.rooms = new Set();

		if (channel.includes('meeting') && isDebug) {
			// When debugging with the 'debug=socket' query string,
			// you can simulate a continuous failing socket connection with the E hotkey
			// and an immediate reconnection with the R hotkey.
			window.addEventListener('keydown', this.debugListener);
		}
	}

	debugHotkey(e: any) {
		if (e.repeat) return;

		if (e.code === 'KeyE') {
			// disconnects after toggling a debug flag to prevent a reconnection
			if (this.connection.connected && !disableReconnect) {
				disableReconnect = true;
				this.connection.disconnect();
			} else if (!this.connection.connected && disableReconnect) {
				// reconnects after toggling the debug flag off
				disableReconnect = false;
				this.reconnect();
			}
		} else if (e.code === 'KeyR') {
			// disconnect and reconnect one second later
			if (this.connection.connected) this.connection.disconnect();
		}
	}

	reconnect = (): void => {
		if (this.shouldDelete) return;
		// create new connection
		this.connection = io(this.endpoint, this.options).connect();

		// set reconnection flag
		this.reconnected = true;

		// reset listeners to match new connection
		this.setupListeners();
		socketLog('Socket', 'Created Connection to', this.channel);
	};

	handleReconnect = () => {
		if (this.shouldDelete) return;
		// will be called after initial connection
		this.connection.emit('join-channel', this.channel);

		// recheck live channels
		this.getLiveChannels();

		// if there is a user account here, rebroadcast that the user is connected
		if (this.user) {
			const roomsToRejoin = Array.from(this.rooms);

			this.setRooms([]);
			this.setRooms(roomsToRejoin);
			this.resetConnectedUsers = true;
			this.user.reconnectFrom = this.user.socketId;
			this.userConnected(this.user);

			socketLog('SocketReconnect', { channel: this.channel, id: this.connection.id, user: this.user });
		}

		// Update connection time
		this.connectionTimestamp = Date.now();

		// Run reconnect function if it exists
		this.onReconnected?.();
	};

	disconnect = (): void => {
		this.shouldDelete = true;
		this.listeners.clear();
		this.connection.removeAllListeners();
		this.connection.disconnect();
		socketLog('SocketDisconnect', { channel: this.channel, id: this.connection.id, user: this.user });
	};

	handleConnection = (): void => {
		if (this.shouldDelete) return;
		this.ready = true;
		this.wasHidden = false;
		socketLog('connection id', this.connection.id);
		// if (!this.channel.startsWith('temp-'))
		setTimeout(() => log({ socket_connected: this.connection.id }));

		// if this is a reconnection, use special reconnect handlers
		if (this.reconnected) {
			this.handleReconnect();
		} else {
			console.log('emitting join-channel on handleConnection')
			this.connection.emit('join-channel', this.channel);
			socketLog('SocketConnect', { channel: this.channel, id: this.connection.id, user: this.user });
			this.onConnected?.();
			socketLog('ConnectionTime', `${Date.now() - this.connectionTimestamp}ms to connect`);
		}
	};

	handleDisconnect = (reason: SocketClass.DisconnectReason): void => {
		const disconnectAsError: Error = {
			name: 'disconnect',
			message: reason,
		};

		this.handleError(disconnectAsError);
	};

	handleError = (e: Error): void => {
		if (this.shouldDelete) return;

		let err: Error | string = e;
		// if message is string, construct new error to get stack trace for datadog
		if (typeof err === 'string') {
			err = new Error(err);
		}
		logger.error(err);

		// if (!this.channel.startsWith('temp-'))
		log({ socket_error: err.message });
		socketLog('SocketError', { error: err, message: err.message });

		// server disconnected, manually disconnect if possible
		this.connection?.disconnect();

		// remove yourself from the connectedUsers array
		if (this.user) store.dispatch(userDisconnected(this.user));

		// debug flag to prevent reconnection
		if (disableReconnect) return;

		setTimeout(this.reconnect, 1000);
	};

	hasListener = (event: string): boolean => {
		if (this.listeners.has(event)) {
			return true;
		}
		return false;
	};

	handleMessage = (message: Message): void | null => {
		socketLog('SocketMessage', message);
		this.listeners.get(message.type)?.(message);
		if (message.data) {
			if (message.data.unelevated) {
				return this.listeners.get('unelevated')?.(message.data.unelevated);
			}
			if (message.data.elevated) {
				return this.listeners.get('elevated')?.(message.data.elevated);
			}
			if (message.data.deletemsg) {
				return this.listeners.get('deletemsg')?.(message.data.deletemsg);
			}
			if (message.data.undeletemsg) {
				return this.listeners.get('undeletemsg')?.(message.data.undeletemsg);
			}
		}
		return null;
	};

	setupListeners = (): void => {
		this.connection.on('connect', this.handleConnection);
		this.connection.on('error', this.handleError);
		this.connection.on('disconnect', this.handleDisconnect);
		this.connection.on('message', this.handleMessage);

		this.connection.on('chat', (message: any) => {
			this.listeners.get('chat-message')?.(message);
		});
		this.connection.on('user-disconnected', (user: ConnectedUser) => {
			this.listeners.get('user-disconnected')?.(user);
		});
		this.connection.on('user-connected', (user: ConnectedUser) => {
			this.listeners.get('user-connected')?.(user);
		});
		this.connection.on('users-connected', (users: ConnectedUser[]) => {
			this.listeners.get('users-connected')?.(users);
		});
		this.connection.on('user-updated', (user: ConnectedUser) => {
			this.listeners.get('user-updated')?.(user);
		});
		this.connection.on('new-room', this.onNewRoom);
		this.connection.on('media-item-update', (mediaItem: MediaItem) => {
			this.listeners.get('media-item-update')?.(mediaItem);
		});
		this.connection.on('join-request', (email: string) => {
			this.listeners.get('join-request')?.(email);
		});
		this.connection.on('join-accepted', (email: string) => {
			this.listeners.get('join-accepted')?.(email);
		});
		this.connection.on('set-media', (mediaItem: MediaItem) => {
			this.listeners.get('set-media')?.(mediaItem);
		});
		this.connection.on('delete-media-item', (id: number) => {
			this.listeners.get('delete-media-item')?.(id);
		});
		this.connection.on('request-isLive-state', (email: string) => {
			this.listeners.get('request-isLive-state')?.(email);
		});
		this.connection.on('isLive-state-update', (message: any) => {
			this.listeners.get('isLive-state-update')?.(message);
		});

		// entities
		entitiesToListenTo.forEach((entityType: string) => {
			// enabler view does not listen for certain entity updates
			if (this.isEnablerView && entitiesIgnoredByEnabler.includes(entityType as EntityType)) return;

			this.connection.on(`entity-update-${entityType}`, (e: Entity) => {
				store.dispatch({ type: e.entityType, payload: e });
			});
		});

		// deleted entities
		deletedEntitiesToListenTo.forEach((entityType: string) => {
			this.connection.on(`entity-deleted-${entityType}`, (e: Entity) => {
				store.dispatch({ type: `${e.entityType}-delete`, payload: e });
			});
		});
	};

	setUpEditListeners = (): void => {
		entitiesToListenTo.forEach((entityType: string) => {
			// enabler view does not listen for certain entity updates
			if (this.isEnablerView && entitiesIgnoredByEnabler.includes(entityType as EntityType)) return;

			this.connection.on(`entity-update-${entityType}-draft`, (e: Entity) => {
				store.dispatch({ type: `${e.entityType}-edit`, payload: e });
			});
		});
	};

	disconnectEditListeners = (): void => {
		entitiesToListenTo.forEach((entityType: string) => {
			this.connection.off(`entity-update-${entityType}-draft`);
		});
	};

	checkJoinRequests = (channel: string) => {
		this.connection.emit('check-join-requests', channel);
	};

	joinRequest = (email: string, channel?: string) => {
		this.connection.emit('join-request', email, channel);
	};

	joinAccepted = (email: string, channel?: string) => {
		this.connection.emit('join-accepted', email, channel);
	};

	handleIncomingChat = (message: ChatMess) => {
		this.listeners.get('chat')?.(message);
	};

	userConnected = (user: ConnectedUser): void => {
		socketLog('sending connected user', user);
		this.connection.emit('set-user', user);
		this.connection.emit('get-users');
		this.user = { ...user, socketId: this.connection.id };
	};

	getUsers = (): void => {
		this.connection.emit('get-users');
	};

	updateUser = (user: ConnectedUser): void => {
		this.connection.emit('update-user', user);
		this.user = { ...user, socketId: this.connection.id };
	};

	addListener = (name: string, func: Function): void => {
		if (this.listeners.has(name)) {
			logger.warn('Listener already exists', name, this.listeners.get(name)?.toString());
		}
		this.listeners.set(name, func);
	};

	removeListener = (name: string): void => {
		this.listeners.delete(name);
	};

	removeAllListeners = (): void => {
		this.listeners.clear();
	};

	sendWaitingMessages = (): void => {
		const len: number = this.waitingMessages.length;
		for (let i = 0; i < len; i += 1) {
			this.sendWaitingMessage(this.waitingMessages[i]);
		}
	};

	sendWaitingMessage = (mess: Message): void => {
		this.sendMessage(mess.type, mess.message);
	};

	sendMessage = (type: string, message: Object, channelOverride?: string) => {
		socketLog('sending message');
		if (!this.ready) {
			socketLog('not ready');
			this.waitingMessages.push({ type, message });
		} else {
			socketLog(
				'emitting message',
				'message',
				{ type, message },
				!channelOverride ? this.channel : channelOverride,
			);
			this.connection.emit(
				'message',
				{ type, message },
				!channelOverride ? this.channel : channelOverride,
			);
		}
	};

	sendRaw = (message: Message): void => {
		this.connection.emit('message', JSON.stringify(message), this.channel);
	};

	sendString = (type: string, message: string): void => {
		this.connection.emit(type, message, this.channel);
	};

	sendChat = (to: string, message: any): void => {
		socketLog('sending message', to, message);
		this.connection.emit('chat', to, message);
	};

	joinChatRoom = (room: string): void => {
		if (!this.rooms.has(room)) {
			this.connection.emit('join-room', room);
			this.rooms.add(room);
		}
	};

	addNewChannel = (room: string): void => {
		if (!this.rooms.has(room)) {
			this.connection.emit('join-room', room);
			this.rooms.add(room);
		}
	};

	getLiveChannels = (): void => {
		this.connection.emit('get-producers');
	};

	leaveRoomChat = (room: string): void => {
		if (this.rooms.has(room)) {
			this.connection.emit('leave-room', room);
			this.rooms.delete(room);
		}
	};

	setRooms = (rooms: string[]): void => {
		const currentRooms = Array.from(this.rooms);
		const diff = difference(rooms, currentRooms);

		rooms.forEach((room) => {
			// this is a newly added room since it was not in this.rooms, so join it
			if (diff.includes(room)) {
				this.connection.emit('join-room', room);
			}
		});

		this.rooms.forEach((room) => {
			// this room is present in the current rooms but not the new ones, so leave it
			if (diff.includes(room)) {
				this.connection.emit('leave-room', room);
			}
		});

		this.rooms = new Set<string>(rooms);
	};

	newRoomCreated = (to: string, room: string): void => {
		this.connection.emit('new-room', to, room);
	};

	onNewRoom = (room: any): void => {
		this.listeners.get('new-room')?.(room);
		this.joinChatRoom(`room.${room.meeting}.${room.id}`);
	};

	sendRoomChat = (room: string, message: any): void => {
		this.connection.emit('room-chat', room, message);
	};

	getCurrentlyWatching = (channel: string): Promise<number> => new Promise((resolve) => {
		this.connection.once('currently-watching', (current: any) => {
			resolve(current.viewers);
		});

		this.connection.emit('get-currently-watching', channel);
	});

	mediaItemUpdate = (mediaItem: MediaItem): void => {
		this.connection.emit('media-item-update', mediaItem);
	};

	setMedia = (media: MediaItem) => {
		this.connection.emit('set-media', media);
	};

	deleteMediaItem = (id: number) => {
		this.connection.emit('delete-media-item', id);
	};
}
